/**
 * Tournament Value Object (VO) Builder
 *
 * Constructs a standardized tournament value object that contains all
 * necessary information for access control and business logic.
 *
 * This ensures consistent data structure across the application and
 * makes it easy to pass tournament state to access control functions.
 */

/**
 * Build a complete Tournament VO from various data sources
 *
 * @param {Object} tournament - Basic tournament data from API
 * @param {Array} rounds - Tournament rounds with games from API
 * @param {Array} participants - Tournament participants
 * @param {Object} userInfo - Current user information
 * @param {Object} additionalData - Any additional context
 * @returns {Object} Complete tournament value object
 *
 * @example
 * const tournamentVO = buildTournamentVO(tournament, apiRounds, participants, currentUser);
 * const canEdit = isEnabled(userId, TOURNAMENT_ENTITIES.TOURNAMENT_EDIT, tournamentVO);
 */
export function buildTournamentVO(
  tournament,
  rounds = [],
  participants = [],
  userInfo = null,
  additionalData = {}
) {
  if (!tournament) {
    return null;
  }

  // Normalize tournament fields (handle both snake_case and camelCase from API)
  const normalizedTournament = {
    // Basic info
    id: tournament.id,
    tournamentName: tournament.tournamentName || tournament.tournament_name,
    status: tournament.status,

    // Organizer info
    organizer_user_id: tournament.organizer_user_id || tournament.organizerUserId,
    organizerUserId: tournament.organizer_user_id || tournament.organizerUserId,

    // Dates
    startDate: tournament.startDate || tournament.startdate,
    endDate: tournament.endDate || tournament.enddate,
    registration_deadline: tournament.registration_deadline || tournament.registrationDeadline,
    registrationDeadline: tournament.registration_deadline || tournament.registrationDeadline,

    // Tournament config
    participantsType: tournament.participantsType || tournament.participants_type,
    participants_type: tournament.participantsType || tournament.participants_type,
    maxParticipants: tournament.maxParticipants || tournament.maxparticipants,
    maxparticipants: tournament.maxParticipants || tournament.maxparticipants,
    schedule_type: tournament.schedule_type || tournament.scheduleType,
    scheduleType: tournament.schedule_type || tournament.scheduleType,

    // Sport info
    sport_type_id: tournament.sport_type_id || tournament.sportTypeId,
    sportTypeId: tournament.sport_type_id || tournament.sportTypeId,

    // Facility
    facility_id: tournament.facility_id || tournament.facilityId,
    facilityId: tournament.facility_id || tournament.facilityId,

    // Venue (Google Places)
    venueName: tournament.venue_name || tournament.venueName || '',
    venueAddress: tournament.venue_address || tournament.venueAddress || '',
    venuePlaceId: tournament.venue_place_id || tournament.venuePlaceId || '',
    venueLat: tournament.venue_lat || tournament.venueLat || null,
    venueLng: tournament.venue_lng || tournament.venueLng || null,

    // Other fields
    description: tournament.description,
    registrationFee: tournament.registrationFee || tournament.registrationfee,
    registrationfee: tournament.registrationFee || tournament.registrationfee,
    difficulty_level: tournament.difficulty_level || tournament.difficultyLevel,
    difficultyLevel: tournament.difficulty_level || tournament.difficultyLevel,
    tournament_image: tournament.tournament_image || tournament.tournamentImage,
    tournamentImage: tournament.tournament_image || tournament.tournamentImage,

    // Keep original object for any unmapped fields
    ...tournament,
  };

  // Process rounds to include full game information
  const processedRounds = rounds.map(round => ({
    ...round,
    // Normalize round fields
    round_number: round.round_number || round.roundNumber,
    roundNumber: round.round_number || round.roundNumber,
    round_name: round.round_name || round.roundName,
    roundName: round.round_name || round.roundName,
    round_type: round.round_type || round.roundType,
    roundType: round.round_type || round.roundType,

    // Process games within round
    games: (round.games || []).map(game => ({
      ...game,
      // Normalize game fields
      tournament_id: game.tournament_id || game.tournamentId,
      tournamentId: game.tournament_id || game.tournamentId,
      round_number: game.round_number || game.roundNumber,
      roundNumber: game.round_number || game.roundNumber,
      match_number: game.match_number || game.matchNumber,
      matchNumber: game.match_number || game.matchNumber,
      event_id: game.event_id || game.eventId,
      eventId: game.event_id || game.eventId,

      // Participant info
      participant_a_type: game.participant_a_type || game.participantAType,
      participant_a_id: game.participant_a_id || game.participantAId,
      participant_a_name: game.participant_a_name || game.participantAName,
      participant_b_type: game.participant_b_type || game.participantBType,
      participant_b_id: game.participant_b_id || game.participantBId,
      participant_b_name: game.participant_b_name || game.participantBName,

      // Status fields
      status: game.status,
      checkin_status: game.checkin_status || game.checkinStatus,
      checkinStatus: game.checkin_status || game.checkinStatus,

      // Event info
      event: game.event,

      // Checkins
      checkins: game.checkins || [],

      // Scores (from pingpong_games or other sport-specific tables)
      participant_a_score: game.participant_a_score,
      participant_b_score: game.participant_b_score,
      winner_info: game.winner_info,
    }))
  }));

  // Process participants
  const processedParticipants = (participants || []).map(p => ({
    ...p,
    // Normalize participant fields
    participant_id: p.participant_id || p.participantId || p.id,
    participantId: p.participant_id || p.participantId || p.id,
    participant_type: p.participant_type || p.participantType,
    participantType: p.participant_type || p.participantType,
    participant_name: p.participant_name || p.participantName || p.name,
    participantName: p.participant_name || p.participantName || p.name,
    team_id: p.team_id || p.teamId,
    teamId: p.team_id || p.teamId,
    team_name: p.team_name || p.teamName,
    teamName: p.team_name || p.teamName,
  }));

  // Build complete VO
  return {
    // Normalized tournament data
    ...normalizedTournament,

    // Rounds and games
    rounds: processedRounds,

    // Participants
    participants: processedParticipants,

    // Computed fields for access control
    _computed: {
      // Total games count
      totalGames: processedRounds.reduce((sum, round) => sum + (round.games?.length || 0), 0),

      // Games by status
      pendingGames: processedRounds.reduce((sum, round) =>
        sum + (round.games?.filter(g => g.status === 'pending').length || 0), 0
      ),
      inProgressGames: processedRounds.reduce((sum, round) =>
        sum + (round.games?.filter(g => g.status === 'in_progress').length || 0), 0
      ),
      completedGames: processedRounds.reduce((sum, round) =>
        sum + (round.games?.filter(g => g.status === 'completed' || g.status === 'Completed').length || 0), 0
      ),

      // Tournament state flags
      hasTournamentStarted: processedRounds.some(round =>
        round.games?.some(game =>
          game.status === 'in_progress' || game.status === 'completed' || game.status === 'Completed'
        )
      ),

      allRoundsCompleted: processedRounds.length > 0 && processedRounds.every(round =>
        round.games?.length > 0 && round.games.every(game =>
          game.status === 'completed' || game.status === 'Completed' || game.checkin_status === 'completed'
        )
      ),

      // Participant counts
      totalParticipants: processedParticipants.length,
      teamParticipants: processedParticipants.filter(p => p.participant_type === 'team').length,
      individualParticipants: processedParticipants.filter(p => p.participant_type === 'user').length,

      // Current user context
      isCurrentUserOrganizer: userInfo?.userId === normalizedTournament.organizer_user_id,
      isCurrentUserParticipant: processedParticipants.some(p =>
        p.participant_id === userInfo?.userId || p.team_id === userInfo?.teamId
      ),
    },

    // Include any additional data passed in
    ...additionalData,
  };
}

/**
 * Update tournament VO with new data
 *
 * Used when specific parts of the tournament change (e.g., a game completes)
 * without needing to rebuild the entire VO from scratch.
 *
 * @param {Object} currentVO - Current tournament VO
 * @param {Object} updates - Partial updates to apply
 * @returns {Object} Updated tournament VO
 *
 * @example
 * // After a game completes
 * const updatedVO = updateTournamentVO(tournamentVO, {
 *   rounds: updatedRounds
 * });
 */
export function updateTournamentVO(currentVO, updates) {
  if (!currentVO) return null;

  // If updating rounds, rebuild computed fields
  if (updates.rounds) {
    return buildTournamentVO(
      { ...currentVO, ...updates },
      updates.rounds,
      currentVO.participants,
      null, // userInfo is already in currentVO._computed
      {}
    );
  }

  // For simple updates, just merge
  return {
    ...currentVO,
    ...updates,
  };
}

/**
 * Refresh tournament VO from API data
 *
 * Call this after any tournament mutation (game complete, registration, etc.)
 * to ensure the VO reflects the latest state.
 *
 * @param {Object} tournament - Latest tournament data from API
 * @param {Array} rounds - Latest rounds data from API
 * @param {Array} participants - Latest participants data
 * @param {Object} userInfo - Current user info
 * @returns {Object} Fresh tournament VO
 *
 * @example
 * // After completing a game
 * const handleGameComplete = async (gameId, result) => {
 *   await api.recordResult(gameId, result);
 *
 *   // Refresh data
 *   const [tournament, rounds] = await Promise.all([
 *     tournamentAPI.getById(tournamentId),
 *     tournamentScheduleAPI.getRounds(tournamentId)
 *   ]);
 *
 *   // Build fresh VO
 *   const freshVO = refreshTournamentVO(tournament, rounds, participants, currentUser);
 *   setTournamentVO(freshVO);
 * };
 */
export function refreshTournamentVO(tournament, rounds, participants, userInfo) {
  return buildTournamentVO(tournament, rounds, participants, userInfo, {});
}

/**
 * Extract game from tournament VO by game ID
 *
 * @param {Object} tournamentVO - Tournament VO
 * @param {number} gameId - Game ID to find
 * @returns {Object|null} Game object or null if not found
 */
export function getGameFromVO(tournamentVO, gameId) {
  if (!tournamentVO?.rounds) return null;

  for (const round of tournamentVO.rounds) {
    const game = round.games?.find(g => g.id === gameId);
    if (game) return game;
  }

  return null;
}

/**
 * Extract round from tournament VO by round number
 *
 * @param {Object} tournamentVO - Tournament VO
 * @param {number} roundNumber - Round number to find
 * @returns {Object|null} Round object or null if not found
 */
export function getRoundFromVO(tournamentVO, roundNumber) {
  if (!tournamentVO?.rounds) return null;
  return tournamentVO.rounds.find(r => r.round_number === roundNumber || r.roundNumber === roundNumber);
}

export default {
  buildTournamentVO,
  updateTournamentVO,
  refreshTournamentVO,
  getGameFromVO,
  getRoundFromVO,
};
